import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Sidebar from './Sidebar';

const PAGE_LABELS = {
  '/admin/dashboard': 'Dashboard',
  '/admin/users':     'Users',
  '/admin/pages':     'Pages Management',
  '/admin/services':  'Services Management',
  '/admin/packages':  'Price Packages',
  '/admin/forms':     'Custom Forms',
  '/admin/testimonials': 'Testimonials',
  '/admin/events':     'Event Gallery',
  '/admin/settings':  'Settings',
  '/admin/reports':   'Reports',
  '/admin/help':      'Help Center',
};

const AdminLayout = () => {
  const { pathname } = useLocation();
  const pageLabel = PAGE_LABELS[pathname] ?? 'Admin Portal';

  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const token = localStorage.getItem('auth_token');
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('auth_user'));
  } catch (e) {}

  if (!token || !user || user.role !== 'admin') {
    return <Navigate to="/admin_login" replace />;
  }

  return (
    <>
      <Toaster position="top-right" />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        * { box-sizing: border-box; }

        .admin-layout {
          display: flex;
          min-height: 100vh;
          background: #f5f7fa;
          font-family: 'Inter', sans-serif;
        }

        .admin-main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          background: #f5f7fa;
        }

        .admin-top-header {
          height: 64px;
          background: #ffffff;
          border-bottom: 1px solid #e8edf3;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 28px;
          position: sticky;
          top: 0;
          z-index: 50;
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
        }

        .admin-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .admin-breadcrumb {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #94a3b8;
        }

        .admin-breadcrumb-sep { color: #cbd5e1; }

        .admin-breadcrumb-current {
          color: #334155;
          font-weight: 600;
        }

        .admin-header-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .admin-header-icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 9px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.18s;
          font-size: 14px;
          position: relative;
        }

        .admin-header-icon-btn:hover {
          background: #f1f5f9;
          color: #334155;
          border-color: #cbd5e1;
        }

        .admin-notif-badge {
          position: absolute;
          top: 7px;
          right: 7px;
          width: 7px;
          height: 7px;
          background: #CFA047;
          border-radius: 50%;
          border: 2px solid #fff;
        }

        .admin-header-divider {
          width: 1px;
          height: 22px;
          background: #e2e8f0;
          margin: 0 4px;
        }

        .admin-user-profile {
          display: flex;
          align-items: center;
          gap: 9px;
          cursor: pointer;
          padding: 5px 12px 5px 5px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          transition: all 0.18s;
        }

        .admin-user-profile:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        .admin-avatar {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: linear-gradient(135deg, #061830, #CFA047);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
          box-shadow: 0 2px 8px rgba(207, 160, 71, 0.25);
          flex-shrink: 0;
        }

        .admin-user-text { display: flex; flex-direction: column; }

        .admin-user-name-header {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          line-height: 1.2;
        }

        .admin-user-role-header {
          font-size: 11px;
          color: #94a3b8;
        }

        .admin-status-dot {
          width: 7px;
          height: 7px;
          background: #CFA047;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(207, 160, 71,0.15);
          margin-left: 2px;
        }

        .admin-content-area {
          flex: 1;
          padding: 28px 32px;
          overflow-y: auto;
        }

        .admin-dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          min-width: 180px;
          padding: 8px;
          z-index: 100;
          animation: slideDown 0.2s ease-out;
        }

        .admin-dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          color: #ef4444;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .admin-dropdown-item:hover {
          background: #fef2f2;
        }
        
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Logout Modal */
        .logout-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.2s ease-out;
        }
        .logout-modal-content {
          background: white;
          padding: 28px 24px;
          border-radius: 16px;
          width: 320px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          text-align: center;
          animation: scaleUp 0.2s ease-out;
        }
        .logout-modal-icon {
          width: 56px; height: 56px; border-radius: 50%;
          background: #fee2e2; color: #ef4444;
          display: flex; align-items: center; justify-content: center;
          font-size: 24px; margin: 0 auto 16px;
        }
        .logout-modal-title { font-size: 18px; font-weight: 700; color: #0f172a; margin: 0 0 8px; }
        .logout-modal-desc { font-size: 14px; color: #64748b; margin: 0 0 24px; line-height: 1.5; }
        .logout-modal-actions { display: flex; gap: 12px; }
        .logout-modal-btn {
          flex: 1; padding: 12px; border-radius: 10px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; font-size: 14px;
        }
        .logout-modal-btn-cancel { background: #f1f5f9; color: #475569; }
        .logout-modal-btn-cancel:hover { background: #e2e8f0; }
        .logout-modal-btn-confirm { background: #ef4444; color: white; }
        .logout-modal-btn-confirm:hover { background: #dc2626; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3); }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleUp { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>

      <div className="admin-layout">
        <Sidebar />
        <div className="admin-main-content">
          <header className="admin-top-header">
            <div className="admin-header-left">
              <div className="admin-breadcrumb">
                <span>Legal Infos</span>
                <span className="admin-breadcrumb-sep">›</span>
                <span>Admin</span>
                <span className="admin-breadcrumb-sep">›</span>
                <span className="admin-breadcrumb-current">{pageLabel}</span>
              </div>
            </div>
            <div className="admin-header-right">
              <div className="admin-user-profile" ref={dropdownRef} onClick={() => setShowDropdown(!showDropdown)} style={{ position: 'relative' }}>
                <div className="admin-avatar">{user.name ? user.name.charAt(0).toUpperCase() : 'A'}</div>
                <div className="admin-user-text">
                  <span className="admin-user-name-header">{user.name || 'Admin User'}</span>
                  <span className="admin-user-role-header">{user.role === 'admin' ? 'Administrator' : 'User'}</span>
                </div>
                <div className="admin-status-dot"></div>
                {showDropdown && (
                  <div className="admin-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-dropdown-item" onClick={() => { setShowDropdown(false); setShowLogoutModal(true); }}>
                      <i className="fa-solid fa-arrow-right-from-bracket"></i> Sign Out
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
          <main className="admin-content-area">
            <Outlet />
          </main>
        </div>
      </div>

      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="logout-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-icon">
              <i className="fa-solid fa-arrow-right-from-bracket"></i>
            </div>
            <h3 className="logout-modal-title">Sign Out</h3>
            <p className="logout-modal-desc">Are you sure you want to sign out of your admin account?</p>
            <div className="logout-modal-actions">
              <button className="logout-modal-btn logout-modal-btn-cancel" onClick={() => setShowLogoutModal(false)}>
                Cancel
              </button>
              <button 
                className="logout-modal-btn logout-modal-btn-confirm" 
                onClick={() => {
                  localStorage.removeItem('auth_token');
                  localStorage.removeItem('auth_user');
                  navigate('/admin_login');
                }}
              >
                Yes, Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminLayout;
